import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Stack,
  Box,
  FormControlLabel,
  Checkbox,
  Switch,
} from '@mui/material';
import { X } from '@phosphor-icons/react';
import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { useInvalidation } from 'api-read-hook';
import useApiCallable from '../../api/use-api-callable';
import { Model } from '../../models';
import { Api } from '../../api';
import { produce } from 'immer';

type QuestionnaireResponse = {
  question: string;
  answer: string | string[] | number | Record<string, string>;
  questionSlug?: string;
  answerSlug?: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  userId: string;
  existingContraceptions: Api.UserStats.V['user']['contraceptions'];
  healthResponses?: QuestionnaireResponse[];
};

export default function EditContraceptionModal({
  open,
  onClose,
  userId,
  existingContraceptions,
  healthResponses = [],
}: Props) {
  const callApi = useApiCallable();
  const { invalidateExact } = useInvalidation();

  const [selectedContraceptions, setSelectedContraceptions] = useState<
    string[]
  >([]);
  const [contraceptionData, setContraceptionData] = useState<
    Record<
      string,
      {
        currentlyUsing: boolean;
        usageLength: number;
        stoppedUsingAt: string | null;
        symptoms: string[];
        customSymptom: string | null;
      }
    >
  >({});
  const [selectedMedications, setSelectedMedications] = useState<string[]>([]);

  // Get options from questionnaires using the model
  const contraceptionOptions = Model.Questionnaire.getOptions(
    'onboarding',
    'contraception',
    'q20',
  );
  const medicationOptions = Model.Questionnaire.getOptions(
    'health',
    'health_conditions',
    'q38',
  );
  const contraceptionQuestion = Model.Questionnaire.getQuestion(
    'onboarding',
    'contraception',
    'q20',
  );
  const contraceptionHelperText = 'Select all that apply';
  const medicationQuestion = Model.Questionnaire.getQuestion(
    'health',
    'health_conditions',
    'q38',
  );

  useEffect(() => {
    if (open) {
      const contraceptionTypes = existingContraceptions.map((c) => c.type);
      setSelectedContraceptions(contraceptionTypes);

      const data: Record<string, any> = {};
      existingContraceptions.forEach((c) => {
        data[c.type] = {
          currentlyUsing: c.currentlyUsing,
          usageLength: c.usageLength,
          stoppedUsingAt: c.stoppedUsingAt,
          symptoms: Array.isArray(c.symptoms)
            ? c.symptoms.map((s: any) => (typeof s === 'string' ? s : s['@id']))
            : [],
          customSymptom: c.customSymptom || null,
        };
      });
      setContraceptionData(data);

      // Load medications from health questionnaire response
      const medicationResponse = healthResponses.find(
        (r) => r.question === medicationQuestion,
      );

      const answer = medicationResponse?.answer;
      if (Array.isArray(answer)) {
        setSelectedMedications(answer);
      } else if (typeof answer === 'string') {
        setSelectedMedications(answer.split(', ').filter(Boolean));
      } else {
        setSelectedMedications([]);
      }
    }
  }, [open, existingContraceptions, healthResponses, medicationQuestion]);

  const handleContraceptionChange = useCallback(
    (contraceptionType: string, checked: boolean) => {
      if (checked) {
        setSelectedContraceptions((prev) => [...prev, contraceptionType]);
        if (!contraceptionData[contraceptionType]) {
          setContraceptionData((prev) => ({
            ...prev,
            [contraceptionType]: {
              currentlyUsing: false,
              usageLength: 0,
              stoppedUsingAt: null,
              symptoms: [],
              customSymptom: null,
            },
          }));
        }
      } else {
        setSelectedContraceptions((prev) =>
          prev.filter((type) => type !== contraceptionType),
        );
        setContraceptionData((prev) => {
          const newState = { ...prev };
          delete newState[contraceptionType];
          return newState;
        });
      }
    },
    [contraceptionData],
  );

  const handleCurrentlyUsingChange = useCallback(
    (contraceptionType: string, currentlyUsing: boolean) => {
      setContraceptionData((prev) => ({
        ...prev,
        [contraceptionType]: {
          ...prev[contraceptionType],
          currentlyUsing,
        },
      }));
    },
    [],
  );

  const handleMedicationChange = useCallback(
    (medication: string, checked: boolean) => {
      if (checked) {
        setSelectedMedications((prev) => [...prev, medication]);
      } else {
        setSelectedMedications((prev) =>
          prev.filter((med) => med !== medication),
        );
      }
    },
    [],
  );

  const handleSave = async () => {
    const contraceptions = selectedContraceptions.map((type) => {
      const data = contraceptionData[type];
      return {
        type,
        currentlyUsing: data?.currentlyUsing || false,
        usageLength: data?.usageLength || 0,
        stoppedUsingAt: data?.stoppedUsingAt || null,
        symptoms: data?.symptoms || [],
        customSymptom: data?.customSymptom || null,
      };
    });

    const updatedHealthResponses = produce(healthResponses, (draft) => {
      const medicationResponseIndex = draft.findIndex(
        (r) => r.question === medicationQuestion,
      );

      if (selectedMedications.length > 0) {
        if (medicationResponseIndex >= 0) {
          draft[medicationResponseIndex].answer = selectedMedications;
        } else {
          draft.push({
            question: medicationQuestion,
            answer: selectedMedications,
          });
        }
      } else if (medicationResponseIndex >= 0) {
        draft.splice(medicationResponseIndex, 1);
      }
    });

    await callApi(`/users/${userId}/as-admin`, {
      method: 'PATCH',
      jsonBody: {
        contraceptions,
      },
    });

    await callApi(`/users/${userId}/questionnaires/before_appointment`, {
      method: 'PATCH',
      jsonBody: {
        responses: updatedHealthResponses,
      },
    });

    invalidateExact(`/users/${userId}/stats`);
    invalidateExact(`/users/${userId}/questionnaires/before_appointment`);
    toast.success('Contraception & Medication updated successfully');
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="h6">Edit Contraception & Medication</Typography>
        <IconButton onClick={onClose} size="small">
          <X size={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Users have the ability to overwrite your changes in the app
          </Typography>

          {/* Contraception Methods */}
          <Box
            sx={{
              bgcolor: 'var(--mui-palette-background-level1)',
              borderRadius: 2,
              p: 3,
            }}
          >
            <Stack spacing={2}>
              <Typography variant="subtitle1">
                {contraceptionQuestion}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {contraceptionHelperText}
              </Typography>

              <Stack spacing={2}>
                {contraceptionOptions.map((option) => (
                  <Box key={option.value}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedContraceptions.includes(
                            option.value,
                          )}
                          onChange={(e) =>
                            handleContraceptionChange(
                              option.value,
                              e.target.checked,
                            )
                          }
                        />
                      }
                      label={option.label}
                    />

                    {/* Show "currently using" toggle for selected contraceptions */}
                    {selectedContraceptions.includes(option.value) && (
                      <Box sx={{ ml: 4, mt: 1 }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={
                                contraceptionData[option.value]
                                  ?.currentlyUsing || false
                              }
                              onChange={(e) =>
                                handleCurrentlyUsingChange(
                                  option.value,
                                  e.target.checked,
                                )
                              }
                              size="small"
                            />
                          }
                          label="I am currently using this"
                          sx={{
                            '& .MuiFormControlLabel-label': {
                              fontSize: '0.875rem',
                              color: 'text.secondary',
                            },
                          }}
                        />
                      </Box>
                    )}
                  </Box>
                ))}
              </Stack>
            </Stack>
          </Box>

          {/* Medications */}
          <Box
            sx={{
              bgcolor: 'var(--mui-palette-background-level1)',
              borderRadius: 2,
              p: 3,
            }}
          >
            <Stack spacing={2}>
              <Typography variant="subtitle1">{medicationQuestion}</Typography>

              <Stack spacing={1}>
                {medicationOptions.map((medication) => (
                  <FormControlLabel
                    key={medication.value}
                    control={
                      <Checkbox
                        checked={selectedMedications.includes(medication.value)}
                        onChange={(e) =>
                          handleMedicationChange(
                            medication.value,
                            e.target.checked,
                          )
                        }
                      />
                    }
                    label={medication.label}
                  />
                ))}
              </Stack>
            </Stack>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleSave} variant="contained">
          Save Changes
        </Button>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}
